import React from 'react';
import {
  GitBranch,
  Camera,
  Users,
  UserPlus,
  BadgeCheck,
  CalendarHeart,
  ShieldCheck,
  Sparkles,
  Check,
  Lock,
  Heart,
  ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * ExperienceShowcase — additive, conversion-focused content used by the
 * public invite/join pages. Renders:
 *   1. Feature showcase (7 emotional pillars)
 *   2. Subscription CTA — TWO plans:
 *        • Monthly $4.99/mo · 3-Day Free Trial · Card Required · Cancel Anytime
 *        • Yearly  $14.99/yr (was $24.99) · Founder's Discount • Ends after first 1,000 families · Best Value
 *   3. Trust + privacy block
 *   4. Social proof line
 *
 * Does NOT touch invite, auth, Stripe or family-graph logic — it just
 * fires the supplied onPrimaryCta callback (which the host page already
 * wires to the existing signup → trial-checkout flow via
 * openMembershipModal / openAuthModal).
 */

export interface ExperienceShowcaseProps {
  onPrimaryCta: () => void;
  primaryLabel?: string;
}

const FEATURES: Array<{
  icon: React.ReactNode;
  title: string;
  body: string;
  tone: string;
}> = [
  {
    icon: <GitBranch className="w-5 h-5" />,
    title: 'Build Your Family Tree',
    body: 'Start with yourself and connect parents, grandparents, cousins, children, and more.',
    tone: 'from-fuchsia-500 to-pink-500',
  },
  {
    icon: <Camera className="w-5 h-5" />,
    title: 'Share Memories',
    body: 'Upload photos, videos, and stories so your family history lives forever.',
    tone: 'from-rose-500 to-amber-500',
  },
  {
    icon: <Users className="w-5 h-5" />,
    title: 'Explore Your Family',
    body: 'Tap any person to see their life, memories, and connections.',
    tone: 'from-purple-500 to-fuchsia-500',
  },
  {
    icon: <UserPlus className="w-5 h-5" />,
    title: 'Invite Your Family',
    body: 'Bring your relatives in and watch your tree grow in real time.',
    tone: 'from-pink-500 to-rose-500',
  },
  {
    icon: <BadgeCheck className="w-5 h-5" />,
    title: 'Claim Your Profile',
    body: 'If someone added you, claim your profile and make it your own.',
    tone: 'from-emerald-500 to-teal-500',
  },
  {
    icon: <CalendarHeart className="w-5 h-5" />,
    title: 'Family Timeline',
    body: 'See birthdays, milestones, and memories in one place.',
    tone: 'from-amber-500 to-orange-500',
  },
  {
    icon: <ShieldCheck className="w-5 h-5" />,
    title: 'Private Family Space',
    body: 'Only your family can see your content — safe and secure.',
    tone: 'from-sky-500 to-indigo-500',
  },
];

const TRIAL_BULLETS = [
  'Full access during trial',
  'No charge until trial ends',
  'Cancel anytime',
  'Unlimited family members & invites',
  'Photos, videos, stories, timeline',
  'Private family vault',
];

const ExperienceShowcase: React.FC<ExperienceShowcaseProps> = ({
  onPrimaryCta,
  primaryLabel = 'Start Free Trial',
}) => {
  return (
    <div className="space-y-10 sm:space-y-14">
      {/* ─────────────── Feature showcase ─────────────── */}
      <section>
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-fuchsia-100 text-fuchsia-700 text-[11px] font-bold uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5" /> What you'll do
          </div>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-bold text-slate-900">
            Everything your family in one place
          </h2>
          <p className="mt-2 text-sm sm:text-base text-slate-600 max-w-xl mx-auto">
            KinsCircle is built to feel like home — fun, simple, and made for the people who matter most.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="group relative rounded-3xl bg-white border border-fuchsia-100 p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition"
            >
              <div
                className={`inline-flex w-11 h-11 rounded-2xl bg-gradient-to-br ${f.tone} text-white items-center justify-center shadow-md mb-3`}
              >
                {f.icon}
              </div>
              <div className="font-bold text-slate-900 text-base">{f.title}</div>
              <div className="mt-1 text-sm text-slate-600 leading-snug">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────────── Subscription CTA — Two plans ─────────────── */}
      <section>
        {/* Section heading */}
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-fuchsia-100 text-fuchsia-700 text-[11px] font-bold uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5" /> Choose your plan
          </div>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-bold text-slate-900">
            Two ways to start
          </h2>
          <p className="mt-2 text-sm sm:text-base text-slate-600 max-w-xl mx-auto">
            Try monthly free for 3 days, or save big with the yearly Founder's
            Discount. <span className="font-semibold text-slate-700">No charge today.</span>
          </p>
        </div>

        {/* Monthly plan — primary trial card */}
        <div className="rounded-3xl overflow-hidden border-2 border-fuchsia-200 shadow-2xl bg-white">
          <div className="bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 px-6 sm:px-10 py-8 text-white text-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 backdrop-blur text-[11px] font-bold uppercase tracking-widest mb-3">
              <Sparkles className="w-3.5 h-3.5" /> Monthly · Free Trial
            </div>
            <h3 className="font-serif text-3xl sm:text-4xl font-bold">
              Start Your 3-Day Free Trial
            </h3>
            <p className="mt-2 text-sm sm:text-base text-white/90 max-w-md mx-auto">
              Full access from day one. Card required to start — no charge today.
              Cancel before your trial ends to avoid being charged.
            </p>

            <div className="mt-5 flex items-end justify-center gap-2 flex-wrap">
              <span className="font-serif text-5xl sm:text-6xl font-extrabold">$4.99</span>
              <span className="text-base text-white/80 pb-2">/month</span>
            </div>
            <div className="mt-1 text-sm text-white/90 font-medium">
              after your 3-day trial · cancel anytime
            </div>
            <div className="mt-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[11px] font-bold uppercase tracking-wide">
              3-Day Free Trial · Card Required · Cancel Anytime
            </div>
          </div>

          <div className="p-6 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-x-6">
            {TRIAL_BULLETS.map((b) => (
              <div key={b} className="flex items-start gap-2 text-sm text-slate-700">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mt-0.5">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </div>
                <span>{b}</span>
              </div>
            ))}
          </div>

          <div className="px-6 sm:px-10 pb-8">
            <Button
              onClick={onPrimaryCta}
              className="w-full h-14 text-base bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 hover:opacity-95 text-white font-semibold gap-2 shadow-xl rounded-2xl"
            >
              <Sparkles className="w-5 h-5" />
              {primaryLabel}
              <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
            <p className="mt-3 text-center text-[11px] text-slate-600 font-medium">
              You will <strong>not be charged today</strong>.
            </p>
            <p className="mt-1 text-center text-[11px] text-slate-500">
              After your 3-day trial, your card is automatically billed{' '}
              <span className="font-semibold text-slate-700">$4.99/month</span>.
              Cancel anytime before the trial ends to avoid charges.
            </p>
          </div>
        </div>

        {/* Yearly plan — Best Value card */}
        <div className="mt-8 sm:mt-10 relative rounded-3xl overflow-hidden border-2 border-amber-400 shadow-xl bg-gradient-to-br from-white via-amber-50/40 to-pink-50/40">
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold flex items-center gap-1 shadow-md">
            <Sparkles className="w-3 h-3" />
            Founder Pricing Ends After 1,000 Families
          </div>
          <div className="p-6 sm:p-8 pt-7 sm:pt-8 flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex-1 text-center sm:text-left">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-extrabold uppercase tracking-widest mb-2">
                Yearly · Founder's Discount • Ends after first 1,000 families
              </div>
              <div className="flex items-end gap-3 flex-wrap justify-center sm:justify-start">
                <div>
                  <span className="font-serif text-4xl sm:text-5xl font-extrabold text-pink-500">$14.99</span>
                  <span className="text-base text-slate-500 ml-1">/year</span>
                </div>
                <span className="text-base text-slate-400 line-through pb-1">$24.99/yr</span>
              </div>
              <p className="mt-1.5 text-xs text-slate-600">
                That's just <span className="font-semibold text-slate-800">$1.25/month</span>,
                billed yearly. Commitment plan — no trial.
              </p>
              <p className="mt-1 text-[11px] text-emerald-700 font-semibold">
                Save over 60% vs monthly • Lock in $14.99/year before pricing increases
              </p>
            </div>
            <Button
              onClick={onPrimaryCta}
              className="h-12 sm:h-14 text-base bg-gradient-to-r from-amber-500 via-orange-500 to-pink-500 hover:opacity-95 text-white font-semibold gap-2 shadow-lg rounded-2xl px-6 sm:px-8 w-full sm:w-auto flex-shrink-0"
            >
              Get Founder's Yearly
              <ArrowRight className="w-5 h-5 ml-1" />
            </Button>
          </div>
        </div>
      </section>



      {/* ─────────────── Trust block ─────────────── */}
      <section>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TrustCard
            icon={<Lock className="w-5 h-5 text-emerald-700" />}
            title="Private and secure"
            body="End-to-end family-only access. No public profiles, no ads, no data brokers."
          />
          <TrustCard
            icon={<ShieldCheck className="w-5 h-5 text-emerald-700" />}
            title="Your data is protected"
            body="Encrypted in transit and at rest. You stay in control of your family's story."
          />
          <TrustCard
            icon={<Users className="w-5 h-5 text-emerald-700" />}
            title="Only your family"
            body="Photos, videos and stories are visible only to verified relatives you approve."
          />
        </div>
      </section>

      {/* ─────────────── Social proof ─────────────── */}
      <section className="text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-fuchsia-100 shadow-sm">
          <Heart className="w-4 h-4 text-pink-500 fill-pink-500" />
          <span className="text-sm text-slate-700">
            Families are building their legacy every day
          </span>
        </div>
      </section>
    </div>
  );
};

const TrustCard: React.FC<{ icon: React.ReactNode; title: string; body: string }> = ({
  icon,
  title,
  body,
}) => (
  <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
    <div className="inline-flex w-10 h-10 rounded-full bg-white items-center justify-center shadow-sm border border-emerald-100 mb-3">
      {icon}
    </div>
    <div className="font-bold text-slate-900 text-sm">{title}</div>
    <div className="text-xs text-slate-600 mt-1 leading-snug">{body}</div>
  </div>
);

export default ExperienceShowcase;
